import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import { withAuthenticator } from "aws-amplify-react";
import "./components/Auth/StyledAuthenticator.css";
import { useSelector, useDispatch } from "react-redux";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import { SnackbarProvider } from "notistack";

import "devextreme/dist/css/dx.common.css";
import "devextreme/dist/css/dx.light.css";

import AppContainer from "./containers/App";
import * as actions from "./store/actions";
import * as colors from "./styles/colors";
import useMixpanelProfileConfig from "./hooks/useMixpanelProfileConfig";
import selectUser from "./store/selectors/getAppUser";
import usePeriodicSync from "./hooks/usePeriodicSync";
import useSubscriptions from "./hooks/useSubscriptions";

const App = () => {
  useMixpanelProfileConfig();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  useSubscriptions(user ? user.serviceProviderId : null);
  usePeriodicSync(user ? user.serviceProviderId : null);
  useEffect(() => {
    dispatch(actions.auth.attemptAuth());
  }, []);
  return (
    <SnackbarProvider
      maxSnack={4}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {user ? (
        <Router>
          <Switch>
            <Route exact path="/" render={() => <Redirect to="/map" />} />
            <Route exact path="/properties" component={AppContainer} />
            <Route exact path="/users" component={AppContainer} />
            <Route exact path="/map" component={AppContainer} />
            <Route exact path="/schedules" component={AppContainer} />
            <Route
              exact
              path="/schedules/create"
              component={AppContainer}
            />
            <Route
              exact
              path="/schedules/:scheduleId"
              component={AppContainer}
            />
            <Route exact path="/divisions" component={AppContainer} />
            <Route
              exact
              path="/map/properties/create"
              component={AppContainer}
            />
            <Route
              exact
              path="/map/properties/:propertyId"
              component={AppContainer}
            />
            <Route
              exact
              path="/map/responses/:instanceId"
              component={AppContainer}
            />
            <Route
              exact
              path="/map/users/create"
              component={AppContainer}
            />
            <Route
              exact
              path="/map/users/:userId"
              component={AppContainer}
            />
            <Route exact path="/map/filter" component={AppContainer} />
            <Route exact path="/reports" component={AppContainer} />
            <Route exact path="/clients" component={AppContainer} />
            <Route exact path="/billing" component={AppContainer} />
            <Route component={() => <div>404 Not found</div>} />
          </Switch>
        </Router>
      ) : (
        <Grid
          style={{ height: "100vh" }}
          container
          direction="column"
          justify="center"
          alignItems="center"
        >
          <Grid item>
            <CircularProgress color="primary" />
          </Grid>
        </Grid>
      )}
    </SnackbarProvider>
  );
};

// Authenticator configuration with custom styling
const authenticatorConfig = {
  signUpConfig: {
    header: "Create Account",
    hideAllDefaults: false,
    defaultCountryCode: "1",
    signUpFields: [
      {
        label: "Email",
        key: "email",
        required: true,
        displayOrder: 1,
        type: "string"
      },
      {
        label: "Password",
        key: "password",
        required: true,
        displayOrder: 2,
        type: "password"
      }
    ]
  },
  signInConfig: {
    header: "Welcome Back",
    hideAllDefaults: false
  },
  confirmSignUpConfig: {
    header: "Confirm Your Account",
    hideAllDefaults: false
  },
  confirmSignInConfig: {
    header: "Confirm Sign In",
    hideAllDefaults: false
  },
  forgotPasswordConfig: {
    header: "Reset Your Password",
    hideAllDefaults: false
  },
  requireNewPasswordConfig: {
    header: "Set New Password",
    hideAllDefaults: false
  }
};

// Custom theme for authenticator
const customTheme = {
  container: {
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif"
  },
  formContainer: {
    textAlign: 'center',
    margin: 0,
    width: '100%',
    maxWidth: '500px'
  },
  formSection: {
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    padding: '48px 40px',
    maxWidth: '450px',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    margin: '0 auto',
    minWidth: 'auto'
  },
  sectionHeader: {
    color: '#1890FF',
    fontSize: '32px',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: '16px',
    letterSpacing: '-0.5px',
    lineHeight: '1.2'
  },
  sectionHeaderHint: {
    color: '#666',
    fontSize: '16px',
    fontWeight: '400',
    textAlign: 'center',
    marginBottom: '40px',
    lineHeight: '1.5'
  },
  sectionBody: {
    marginBottom: '20px'
  },
  formField: {
    marginBottom: '16px'
  },
  sectionFooter: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  sectionFooterPrimaryContent: {
    width: '100%',
    marginBottom: '16px'
  },
  sectionFooterSecondaryContent: {
    width: '100%',
    textAlign: 'center'
  }
};

export default withAuthenticator(App, {
  ...authenticatorConfig,
  theme: customTheme
});
